import {Button, Radio} from '@wandb/weave/components';
import {IconLightbulbInfo} from '@wandb/weave/components/Icon';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {Banner} from '../../../../components/Banner/Banner';
import {BANNER_VARIANTS} from '../../../../components/Banner/Banner.styles';
import {ClearSettingsRows} from '../components/ClearSettingsRow';
import {SettingLabel} from '../components/SettingLabel';
import {RunColorOptions} from '../types';
import {SettingOverrideCounts} from './../../../../state/views/workspaceSettings/settingOverrideUtils';

export type SemanticLegendRunColorOptionsProps = {
  className?: string;
  hasActiveGrouping: boolean;
  runColorOptions: RunColorOptions;
  numOverrides?: SettingOverrideCounts;
  onRunColorOptionsChange: (value: RunColorOptions) => void;
};

export const SemanticLegendRunColorOptions = ({
  className,
  hasActiveGrouping,
  runColorOptions,
  onRunColorOptionsChange,
}: SemanticLegendRunColorOptionsProps) => {
  return (
    <fieldset className={twMerge('text-moon-800', className)}>
      <div className="flex items-center justify-between">
        <div className="center mb-4 flex flex-col">
          <div className="flex items-center gap-4">
            <SettingLabel className="w-fit" text="Run colors" />

            <Button
              size="small"
              variant="ghost"
              icon="info"
              tooltipProps={{className: 'w-[20rem]'}}
              tooltip={
                <>
                  <div className="mb-12">
                    Use the original run colors assigned to this project, or
                    recolor runs based on their value for a given metric key.
                  </div>
                </>
              }
            />
          </div>
        </div>
      </div>
      {hasActiveGrouping && (
        <Banner variant={BANNER_VARIANTS.INFO} className="mb-12">
          <div className="flex items-center gap-12">
            <div>
              <IconLightbulbInfo height={24} />
            </div>
            <div className="text-moon-600">
              <span className="font-semibold">
                Metric-based colors are currently disabled because run grouping
                is active.
              </span>{' '}
              To use semantic colors, please remove grouping.
            </div>
          </div>
        </Banner>
      )}
      <Radio.Root
        aria-label="Run colors"
        name="run-colors"
        onValueChange={onRunColorOptionsChange}
        value={
          hasActiveGrouping
            ? RunColorOptions.OriginalProjectColors
            : runColorOptions
        }>
        <div
          className="flex items-center justify-start"
          data-test="original-project-colors">
          <Radio.Item
            id="original-project-colors"
            value={RunColorOptions.OriginalProjectColors}>
            <Radio.Indicator />
          </Radio.Item>
          <label htmlFor="original-project-colors">
            <span className="mx-8">Original project colors</span>
          </label>
        </div>
        <div
          className={twMerge(
            'flex items-center justify-start',
            hasActiveGrouping && 'text-moon-500'
          )}
          data-test="metric-based-colors">
          <Radio.Item
            id="metric-based-colors"
            value={RunColorOptions.MetricBasedColors}
            disabled={hasActiveGrouping}>
            <Radio.Indicator />
          </Radio.Item>
          <label htmlFor="metric-based-colors">
            <span className="mx-8">Metric based colors</span>
          </label>
        </div>
      </Radio.Root>
      <ClearSettingsRows rows={[]} />
    </fieldset>
  );
};
